/* eslint-disable @typescript-eslint/no-explicit-any */
import { parseCookies } from '@utils';
import { App } from 'antd';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { sdk } from '@/services';
const CourseService = sdk.getService('CourseService');

const API_URL = import.meta.env['VITE_API_URL'] as string;

const DOWNLOAD_TIMEOUT_MS = 180000;

const isReactNativeWebView = (): boolean =>
  typeof window !== 'undefined' && window.isReactNativeWebView === true;

const buildDownloadUrl = (courseId: string, folderId?: string): string => {
  const base = `${API_URL}/courses/compress-pdfs/${courseId}`;

  return folderId ? `${base}?folderId=${encodeURIComponent(folderId)}` : base;
};

interface UseDownloadMaterialReturn {
  isDownloading: boolean;
  getMaterialCourse: (fileName: string) => Promise<void> | null;
  getMaterialFolder: (folderId: string, fileName: string) => Promise<void> | null;
}

export const useDownloadMaterial = (courseId?: string): UseDownloadMaterialReturn => {
  const { message } = App.useApp();

  const { t } = useTranslation('StudentCourseTranslations');
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadNative = useCallback((downloadUrl: string, fileName: string): Promise<void> => {
    const { token } = parseCookies();
    const downloadId = `dl_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

    if (!window.__downloadResolvers) window.__downloadResolvers = {};

    if (!window.__resolveDownload) {
      window.__resolveDownload = (id, success, error) => {
        const resolver = window.__downloadResolvers?.[id];
        if (!resolver) return;
        if (success) resolver.resolve();
        else resolver.reject(new Error(error ?? 'Download failed'));
        delete window.__downloadResolvers?.[id];
      };
    }

    return new Promise<void>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        const resolver = window.__downloadResolvers?.[downloadId];
        if (resolver) {
          delete window.__downloadResolvers?.[downloadId];
          reject(new Error('Download timed out'));
        }
      }, DOWNLOAD_TIMEOUT_MS);

      window.__downloadResolvers![downloadId] = {
        resolve: () => {
          clearTimeout(timeoutId);
          delete window.__downloadResolvers?.[downloadId];
          resolve();
        },
        reject: (err: Error) => {
          clearTimeout(timeoutId);
          delete window.__downloadResolvers?.[downloadId];
          reject(err);
        }
      };

      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          type: 'DOWNLOAD_FILE',
          downloadId,
          url: downloadUrl,
          fileName,
          mimeType: 'application/zip',
          authToken: token
        })
      );
    });
  }, []);

  const downloadWeb = useCallback(
    async (fetcher: Promise<any>, fileName: string): Promise<void> => {
      const response = await fetcher;
      if (!response) throw new Error('No data');

      const zipBlob = new Blob([response], { type: 'application/zip' });
      const blobUrl = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    },
    []
  );

  const getMaterialCourse = useCallback(
    (fileName: string): Promise<void> | null => {
      if (!courseId) return null;

      setIsDownloading(true);

      const download = isReactNativeWebView()
        ? downloadNative(buildDownloadUrl(courseId), fileName)
        : downloadWeb(CourseService.downloadMaterial(courseId), fileName);

      return download
        .catch(() => {
          message.error(t('DOWNLOAD_MATERIAL_ERROR'));
        })
        .finally(() => setIsDownloading(false));
    },
    [courseId, downloadNative, downloadWeb, t]
  );

  const getMaterialFolder = useCallback(
    (folderId: string, fileName: string): Promise<void> | null => {
      if (!courseId) return null;

      setIsDownloading(true);

      const download = isReactNativeWebView()
        ? downloadNative(buildDownloadUrl(courseId, folderId), fileName)
        : downloadWeb(CourseService.downloadMaterial(courseId, folderId), fileName);

      return download
        .catch(() => {
          message.error(t('DOWNLOAD_MATERIAL_ERROR'));
        })
        .finally(() => setIsDownloading(false));
    },
    [courseId, downloadNative, downloadWeb, t]
  );

  return {
    isDownloading,
    getMaterialCourse,
    getMaterialFolder
  };
};
